<template>
  <section v-for="variant in variants" :key="variant.id">
    <div
      class="transition-all text-sm border-t px-3 py-3 justify-between cursor-pointer"
      @click="toggleVariant(variant.id)"
    >
      <button class="transition-all uppercase tracking-wider text-left inline-flex items-center flex-shrink">
        {{ variant.name }}
        <div class="inline-block ml-1 w-4 h-4 flex-shrink-0">
          <svg
            class="transition-all"
            v-show="openVariantId !== variant.id"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
          >
            <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
          </svg>
          <svg
            class="transition-all"
            v-show="openVariantId === variant.id"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
          >
            <path d="M10.707 7.05L10 6.343 4.343 12l1.414 1.414L10 9.172l4.243 4.242L15.657 12z" />
          </svg>
        </div>
      </button>
      <div
        v-if="selectedOptionsForVariant(variant).length > 0"
        v-show="openVariantId !== variant.id"
        class="transition-all flex flex-shrink-0 flex-wrap min-w-[5em] items-center justify-beginning gap-2 mt-2"
      >
        <div
          v-for="vo in selectedOptionsForVariant(variant)"
          :key="vo.id"
          class="transition-all flex-inline bg-gray-brand-200 text-gray-brand-700 rounded-lg text-xs font-manrope font-semibold tracking-tight px-2 py-1 flex-none"
        >
          {{ vo.name }}
          <span v-if="quantityForVariantOption(vo.id) > 1">x {{ quantityForVariantOption(vo.id) }}</span>
        </div>
      </div>
    </div>
    <div v-show="openVariantId === variant.id" class="transition-all px-3 pb-1">
      <div v-if="isCustomQuantities(variant)" class="flex flex-wrap items-center gap-2 pb-2">
        <V2ProductVariantOptionCustomQuantity
          v-for="vo in variant.variant_options"
          :key="vo.id"
          class="w-full"
          :variant-option="vo"
          :variant="variant"
          :submittable-cart-item="submittableCartItem"
          :show-price="showPrice"
          :product="product"
          @quantity-set="(vo, qty) => onQuantitySet(variant, vo, qty)"
          @unselected="(vo) => onUnselected(variant, vo)"
        />
      </div>
      <div v-else class="flex flex-wrap items-center">
        <V2ProductVariantOption
          v-for="vo in variant.variant_options"
          :key="vo.id"
          class="px-4 py-2 border text-sm hover:border-gray-brand-600 mr-2 mb-2 w-full"
          :class="{ 'rounded-2xl': isChooseOne(variant), 'rounded-md': !isChooseOne(variant) }"
          :variant-option="vo"
          :variant="variant"
          :submittable-cart-item="submittableCartItem"
          :show-price="showPrice"
          :product="product"
          :pricing-tiers="pricingTiers"
          @selected="(vo) => onSelected(variant, vo)"
          @unselected="(vo) => onUnselected(variant, vo)"
        />
      </div>
    </div>
    <div
      v-if="showMinimumNotice(variant) || showMaximumNotice(variant)"
      class="transition-all text-sm px-3 pb-3 justify-between cursor-pointer"
      @click="toggleVariant(variant.id)"
    >
      <div
        v-if="showMinimumNotice(variant)"
        class="transition-all w-full mt-2 py-1 px-2 rounded-md bg-red-50 text-red-900 border border-red-900 text-center"
      >
        Select {{ needToAddCount(variant) }} more
      </div>
      <div
        v-else-if="showMaximumNotice(variant)"
        class="transition-all w-full mt-2 py-1 px-2 rounded-md bg-red-50 text-red-900 border border-red-900 text-center"
      >
        Remove {{ needToRemoveCount(variant) }} options
      </div>
    </div>
  </section>
</template>

<script>
import { inject } from 'vue'
import V2ProductVariantOption from '@/components/v2/V2ProductVariantOption'
import V2ProductVariantOptionCustomQuantity from '@/components/v2/V2ProductVariantOptionCustomQuantity'
import { posthog, getCommonTrackingProperties } from '@/composables/usePosthog'

export default {
  name: 'V2ProductVariants',

  components: { V2ProductVariantOption, V2ProductVariantOptionCustomQuantity },

  setup() {
    const v2UpdateCartItem = inject('v2UpdateCartItem', null)
    return { v2UpdateCartItem }
  },

  props: {
    variants: {
      type: Array,
      required: true
    },
    product: {
      type: Object,
      required: true
    },
    submittableCartItem: {
      type: Object,
      default: null
    },
    showPrice: {
      type: Boolean,
      default: true
    },
    pricingTiers: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      openVariantId: null
    }
  },

  computed: {
    submittableCartItemQuantity() {
      return this.submittableCartItem?.quantity || 0
    }
  },

  watch: {
    submittableCartItemQuantity: {
      handler(newValue, oldValue) {
        if ((oldValue == null || oldValue == 0) && newValue > 0) {
          const customQtyVariant = this.variants.find((v) => this.isCustomQuantities(v))
          this.openVariantId = customQtyVariant ? customQtyVariant.id : null
        }
      },
      immediate: true
    }
  },

  methods: {
    toggleVariant(id) {
      this.openVariantId = this.openVariantId === id ? null : id
    },
    isChooseOne(variant) {
      return variant.quantity_option === 'choose_one'
    },
    isCustomQuantities(variant) {
      if (variant.quantity_option === 'choose_one') return false
      return variant.quantity_option === 'custom_quantities' || variant.min > 0 || variant.max > 0
    },
    selectedOptionsForVariant(variant) {
      if (!this.submittableCartItem?.cart_item_variant_options_attributes) return []
      const selectedIds = this.submittableCartItem.cart_item_variant_options_attributes
        .filter((attr) => attr.variant_id == variant.id)
        .map((attr) => attr.variant_option_id)
      return variant.variant_options.filter((vo) => selectedIds.includes(vo.id))
    },
    quantityForVariantOption(voId) {
      if (!this.submittableCartItem?.cart_item_variant_options_attributes) return 0
      return this.submittableCartItem.cart_item_variant_options_attributes
        .filter((x) => x.variant_option_id == voId)
        .reduce((sum, x) => sum + (x.quantity || 0), 0)
    },
    totalQuantityForVariant(variant) {
      if (!this.submittableCartItem?.cart_item_variant_options_attributes) return 0
      return this.submittableCartItem.cart_item_variant_options_attributes
        .filter((x) => x.variant_id == variant.id)
        .reduce((sum, x) => sum + (x.quantity || 0), 0)
    },
    showMinimumNotice(variant) {
      const total = this.totalQuantityForVariant(variant)
      return (
        total < (variant.min || 0) &&
        variant.quantity_option === 'custom_quantities' &&
        this.needToAddCount(variant) > 0 &&
        (this.submittableCartItem?.quantity || 0) > 0
      )
    },
    showMaximumNotice(variant) {
      const total = this.totalQuantityForVariant(variant)
      return (
        total > (variant.max || 100) &&
        variant.quantity_option === 'custom_quantities' &&
        this.needToRemoveCount(variant) > 0 &&
        (this.submittableCartItem?.quantity || 0) > 0
      )
    },
    needToAddCount(variant) {
      return (variant.min || 0) - this.totalQuantityForVariant(variant)
    },
    needToRemoveCount(variant) {
      return this.totalQuantityForVariant(variant) - (variant.max || 100)
    },
    onSelected(variant, variantOption) {
      posthog.capture('variant_option_selected', {
        ...getCommonTrackingProperties(this),
        variant_option_name: variantOption.name,
        variant_option_id: variantOption.id
      })

      let attrs = [...(this.submittableCartItem?.cart_item_variant_options_attributes || [])]
      // Remove duplicates of this option
      attrs = attrs.filter((a) => a.variant_option_id != variantOption.id)
      // If choose_one, remove other options for this variant
      if (this.isChooseOne(variant)) {
        attrs = attrs.filter((a) => a.variant_id != variant.id)
      }
      attrs.push({
        variant_option_id: variantOption.id,
        variant_id: variant.id,
        quantity: 1
      })

      const updatedItem = {
        ...this.submittableCartItem,
        cart_item_variant_options_attributes: attrs,
        quantity: Math.max(this.submittableCartItem?.quantity || 0, 1)
      }
      if (this.v2UpdateCartItem) {
        this.v2UpdateCartItem(updatedItem)
      } else {
        this.$store.dispatch('updateSubmittableCart', { submittableCartItem: updatedItem })
      }
    },
    onUnselected(variant, variantOption) {
      posthog.capture('variant_option_removed', {
        ...getCommonTrackingProperties(this),
        variant_option_name: variantOption.name,
        variant_option_id: variantOption.id
      })

      const attrs = (this.submittableCartItem?.cart_item_variant_options_attributes || []).filter(
        (a) => a.variant_option_id != variantOption.id
      )
      const updatedItem = {
        ...this.submittableCartItem,
        cart_item_variant_options_attributes: attrs
      }
      if (this.v2UpdateCartItem) {
        this.v2UpdateCartItem(updatedItem)
      } else {
        this.$store.dispatch('updateSubmittableCart', { submittableCartItem: updatedItem })
      }
    },
    onQuantitySet(variant, variantOption, quantity) {
      posthog.capture('variant_option_with_quantity_set', {
        ...getCommonTrackingProperties(this),
        variant_option_name: variantOption.name,
        variant_option_id: variantOption.id,
        count: quantity
      })

      let attrs = [...(this.submittableCartItem?.cart_item_variant_options_attributes || [])]
      // Remove existing entry for this option
      attrs = attrs.filter((a) => a.variant_option_id != variantOption.id)
      // Add with new quantity
      attrs.push({
        variant_option_id: variantOption.id,
        variant_id: variant.id,
        quantity: quantity
      })

      const updatedItem = {
        ...this.submittableCartItem,
        cart_item_variant_options_attributes: attrs,
        quantity: Math.max(this.submittableCartItem?.quantity || 0, 1)
      }
      if (this.v2UpdateCartItem) {
        this.v2UpdateCartItem(updatedItem)
      } else {
        this.$store.dispatch('updateSubmittableCart', { submittableCartItem: updatedItem })
      }
    }
  }
}
</script>
